import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../lib/supabase';

/**
 * APEX v8.15 — AuthContext
 *
 * Encapsule la logique d'authentification :
 *   - état `user` et `authLoading`
 *   - subscription `onAuthChange` (et son cleanup)
 *   - fonction `signOut`
 *
 * Comportement préservé depuis App.jsx :
 *   - Ne re-render QUE sur changement d'identité réel
 *     (TOKEN_REFRESHED qui arrive toutes les heures est ignoré)
 *   - Comparaison par ID pour éviter re-renders sur référence différente
 */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    auth.getUser().then(u => {
      setUser(u);
      setAuthLoading(false);
    });
    const { data: { subscription } } = auth.onAuthChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
        const newUser = session?.user || null;
        setUser(prev => {
          if (!prev && !newUser) return prev;
          if (prev?.id === newUser?.id) return prev;
          return newUser;
        });
      }
    });
    return () => subscription?.unsubscribe();
  }, []);

  const signOut = async () => {
    await auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, authLoading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook d'accès au context. Throw si utilisé en dehors du Provider.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
